import { useState } from "react";
import { Link } from "react-router-dom";
import { IndianRupee, Percent, Wallet, Clock, CheckCircle2, Search, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApi } from "@/hooks/useApi";
import { PageHeader, StatCard, LoadingGrid, ErrorState, EmptyState, StatusBadge, Pager, Field } from "@/components/common";
import { Sel, Table, td } from "@/components/admin-ui";
import { api, errMsg } from "@/lib/api";
import { inr, fdate } from "@/lib/format";

export function AdminEarnings() {
  const [page, setPage] = useState(1);
  const { data, loading, error, reload } = useApi("/admin/earnings", { page });
  if (loading && !data) return <LoadingGrid count={5} className="grid grid-cols-2 lg:grid-cols-5 gap-4" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const s = data.summary;
  return (
    <div>
      <PageHeader eyebrow="Finance" title="Earnings" desc="Aggregated from all non-cancelled brand deals." />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 stagger mb-8">
        <StatCard label="Total deal value" value={inr(s.deal_value)} icon={IndianRupee} testid="ae-deal-value" accent />
        <StatCard label="Commission" value={inr(s.commission)} icon={Percent} testid="ae-commission" />
        <StatCard label="Creator payout" value={inr(s.payout)} icon={Wallet} testid="ae-payout" />
        <StatCard label="Pending" value={inr(s.pending)} icon={Clock} testid="ae-pending" />
        <StatCard label="Paid" value={inr(s.paid)} icon={CheckCircle2} testid="ae-paid" />
      </div>
      {data.items.length === 0 ? <EmptyState icon={Wallet} title="No earnings yet." testid="admin-earnings-empty" /> : (
        <Table testid="earnings-table" head={["Creator", "Deals", "Deal value", "Commission", "Payout", "Paid", "Pending"]}>
          {data.items.map((r) => (
            <tr key={r.creator_id} data-testid={`earnings-row-${r.creator_id}`}>
              <td className={td}><Link to={`/admin/creators/${r.creator_id}`} className="hover:text-primary"><span className="block font-medium">{r.creator_name}</span><span className="text-xs text-muted-foreground">@{r.instagram_username}</span></Link></td>
              <td className={`${td} num`}>{r.deals}</td>
              <td className={`${td} num`}>{inr(r.deal_value)}</td>
              <td className={`${td} num`}>{inr(r.commission)}</td>
              <td className={`${td} num text-primary`}>{inr(r.payout)}</td>
              <td className={`${td} num`}>{inr(r.paid)}</td>
              <td className={`${td} num`}>{inr(r.pending)}</td>
            </tr>
          ))}
        </Table>
      )}
      <Pager page={page} total={data.total} onPage={setPage} />
    </div>
  );
}

function PaymentDialog({ p, onClose, onSaved }) {
  const [f, setF] = useState({ status: p.status, reference: p.reference || "", payment_date: p.payment_date || "", note: p.note || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/payments/${p.id}`, f); toast.success("Payment updated · creator notified"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border rounded-2xl" data-testid="payment-dialog">
        <DialogHeader><DialogTitle className="font-display">{p.creator_name} · {inr(p.amount)}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">{p.brand_name} — {p.campaign_name}</p>
        <div className="space-y-3">
          <Field label="Status"><Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} options={["PENDING", "PROCESSING", "PAID", "FAILED"].map((s) => [s, s])} testid="payment-status-select" className="bg-background" /></Field>
          <Field label="Payment reference / UTR"><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="rounded-xl bg-background" data-testid="payment-reference-input" /></Field>
          <Field label="Payment date"><Input type="date" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} className="rounded-xl bg-background" data-testid="payment-date-input" /></Field>
          <Field label="Internal note"><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="rounded-xl bg-background" data-testid="payment-note-input" /></Field>
          <Button onClick={save} disabled={busy} className="rounded-full w-full" data-testid="payment-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save payment"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AdminPayments() {
  const [f, setF] = useState({ status: "", q: "", page: 1 });
  const [edit, setEdit] = useState(null);
  const { data, loading, error, reload } = useApi("/admin/payments", Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "")));
  return (
    <div>
      <PageHeader eyebrow="Finance" title="Payments" desc="One payment record per brand deal. Creators are notified on every status change." />
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value, page: 1 })} placeholder="Search creator, brand or reference" className="h-10 pl-10 rounded-xl bg-card" data-testid="payments-search-input" />
        </div>
        <Sel value={f.status} onChange={(v) => setF({ ...f, status: v, page: 1 })} options={[["", "All statuses"], ...["PENDING", "PROCESSING", "PAID", "FAILED"].map((s) => [s, s])]} testid="payments-status-filter" />
      </div>
      {loading && !data ? <LoadingGrid count={5} className="space-y-2" h="h-14" /> : error ? <ErrorState message={error} onRetry={reload} /> :
        data.items.length === 0 ? <EmptyState icon={CreditCard} title="No payments yet." desc="Payments are created automatically with each brand deal." testid="admin-payments-empty" /> : (
          <Table testid="payments-table" head={["Creator", "Brand / Campaign", "Amount", "Status", "Reference", "Date", ""]}>
            {data.items.map((p) => (
              <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                <td className={`${td} font-medium`}>{p.creator_name}</td>
                <td className={td}><span className="block">{p.brand_name}</span><span className="text-xs text-muted-foreground">{p.campaign_name}</span></td>
                <td className={`${td} num text-primary`}>{inr(p.amount)}</td>
                <td className={td}><StatusBadge status={p.status} testid={`payment-status-${p.id}`} /></td>
                <td className={`${td} text-muted-foreground`}>{p.reference || "—"}</td>
                <td className={`${td} text-muted-foreground`}>{p.payment_date ? fdate(p.payment_date) : "—"}</td>
                <td className={td}><Button size="sm" variant="outline" className="rounded-full" onClick={() => setEdit(p)} data-testid={`payment-edit-${p.id}`}>Update</Button></td>
              </tr>
            ))}
          </Table>
        )}
      {data && <Pager page={f.page} total={data.total} onPage={(pg) => setF({ ...f, page: pg })} />}
      {edit && <PaymentDialog p={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
